'use client';

import { confirmChatRoute } from '@/app/api/chat/confirm/route.def';
import {
  applyChatEvent,
  emptyTurn,
  turnHasContent,
  type ConfirmState,
  type ThreadItem,
  type TurnItem,
} from '@/components/chat/thread-model';
import { AbortError } from '@/domains/common/abort';
import type { ChatEvent } from '@/domains/common/api';
import { streamApi, type StreamHandle } from '@/domains/common/api-client';
import { useReducer, useRef, useState, type ReactNode } from 'react';

interface UseMatildaThreadOpts {
  agentName: string;
  initialItems: () => ThreadItem[];
  // Append a local "Confirmed: …" user bubble on chip clicks. The sponsor
  // pane sets this because /api/chat/confirm persists the same echo; the
  // global pane stays ephemeral and skips it.
  echoConfirm: boolean;
  // The server reloads the persisted thread for model context, so the panel
  // only sends the new message body — history is not threaded through here.
  openStream: (body: string, gracefulFallback: () => void) => StreamHandle<ChatEvent>;
  onRefresh: () => void;
  onToolUse?: (name: string) => void;
  // Fired with the sub-agent id when a spawn_subagent tool result streams in, so
  // the panel can watch it and surface a live note when it terminates.
  onSubagentSpawned?: (id: string) => void;
}

export interface MatildaThread {
  items: ThreadItem[];
  streaming: boolean;
  send: (body: string) => void;
  stop: () => void;
  reset: (items: ThreadItem[]) => void;
  confirm: (turnId: string, segmentId: string, recommendation: 'confirm' | 'reject') => Promise<void>;
  appendNote: (note: { agent: string; body: ReactNode; tag?: string }) => void;
  clearNotes: (tag: string) => void;
}

export function useMatildaThread(opts: UseMatildaThreadOpts): MatildaThread {
  // Read opts through a per-render-refreshed ref so callers may capture
  // `send` etc. in mount-once effects without freezing openStream's
  // closure (pathname, screen context) at first render.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  // Items live in a ref (mutated via `update`) with a manual re-render
  // trigger, so stream callbacks always read/write current state — no
  // positional indexes captured in stale closures.
  const itemsRef = useRef<ThreadItem[] | null>(null);
  if (itemsRef.current === null) itemsRef.current = opts.initialItems();
  const [, bumpRender] = useReducer((c: number) => c + 1, 0);
  const [streaming, setStreaming] = useState(false);
  const streamingRef = useRef(false);
  const handleRef = useRef<StreamHandle<ChatEvent> | null>(null);
  const queueRef = useRef<{ id: string; body: string }[]>([]);
  const idRef = useRef(0);

  function nextId(): string {
    idRef.current += 1;
    return `local-${idRef.current}`;
  }

  function update(fn: (cur: ThreadItem[]) => ThreadItem[]): void {
    itemsRef.current = fn(itemsRef.current ?? []);
    bumpRender();
  }

  function updateTurn(turnId: string, fn: (turn: TurnItem) => TurnItem): void {
    update(cur => cur.map(item => (item.id === turnId && item.kind === 'turn' ? fn(item) : item)));
  }

  function setStreamingBoth(value: boolean): void {
    streamingRef.current = value;
    setStreaming(value);
  }

  function setConfirmState(turnId: string, segmentId: string, state: ConfirmState): void {
    updateTurn(turnId, turn => ({
      ...turn,
      segments: turn.segments.map(seg =>
        seg.id === segmentId && seg.kind === 'confirm' ? { ...seg, state } : seg,
      ),
    }));
  }

  function failTurn(turnId: string): void {
    updateTurn(turnId, turn => ({
      ...turn,
      segments: [...turn.segments, { kind: 'text', id: nextId(), body: 'Something went wrong' }],
      liveStatus: null,
    }));
    setStreamingBoth(false);
  }

  async function streamIntoTurn(turnId: string, handle: StreamHandle<ChatEvent>): Promise<void> {
    handleRef.current = handle;
    try {
      for await (const frame of handle.data) {
        const event = frame.data;
        if (event.kind === 'refresh') {
          optsRef.current.onRefresh();
          continue;
        }
        if (event.kind === 'tool_use') optsRef.current.onToolUse?.(event.name);
        if (event.kind === 'tool_result' && event.meta?.subagent_id) {
          optsRef.current.onSubagentSpawned?.(event.meta.subagent_id);
        }
        updateTurn(turnId, turn => applyChatEvent(turn, event, nextId));
      }
    } catch (e) {
      AbortError.rethrowIfNotAbort(e);
      updateTurn(turnId, turn => ({ ...turn, stopped: true }));
    } finally {
      handleRef.current = null;
      update(cur =>
        cur
          .map(item => (item.id === turnId && item.kind === 'turn' ? { ...item, streaming: false } : item))
          .map(item => (item.id === turnId && item.kind === 'turn' ? { ...item, liveStatus: null } : item))
          .filter(item => item.id !== turnId || item.kind !== 'turn' || turnHasContent(item)),
      );
      setStreamingBoth(false);
      flushQueue();
    }
  }

  async function runTurn(body: string): Promise<void> {
    const turnId = nextId();
    setStreamingBoth(true);
    update(cur => [...cur, emptyTurn(turnId, optsRef.current.agentName)]);
    let handle: StreamHandle<ChatEvent>;
    try {
      handle = optsRef.current.openStream(body, () => failTurn(turnId));
    } catch {
      // openStream threw before producing a stream — recover the same way
      // streamIntoTurn's finally would, so streaming doesn't latch on and
      // the queue keeps draining.
      failTurn(turnId);
      flushQueue();
      return;
    }
    await streamIntoTurn(turnId, handle);
  }

  function flushQueue(): void {
    const next = queueRef.current.shift();
    if (!next) return;
    update(cur =>
      cur.map(item => (item.id === next.id && item.kind === 'user' ? { ...item, queued: false } : item)),
    );
    void runTurn(next.body);
  }

  function send(body: string): void {
    const value = body.trim();
    if (!value) return;
    const id = nextId();
    const queued = streamingRef.current;
    update(cur => [...cur, { kind: 'user', id, time: 'just now', body: value, queued }]);
    if (queued) {
      queueRef.current.push({ id, body: value });
      return;
    }
    void runTurn(value);
  }

  function stop(): void {
    handleRef.current?.stop();
  }

  function reset(items: ThreadItem[]): void {
    handleRef.current?.stop();
    handleRef.current = null;
    queueRef.current = [];
    setStreamingBoth(false);
    itemsRef.current = items;
    bumpRender();
  }

  async function confirm(
    turnId: string,
    segmentId: string,
    recommendation: 'confirm' | 'reject',
  ): Promise<void> {
    if (streamingRef.current) return;
    const owner = (itemsRef.current ?? []).filter(
      (item): item is TurnItem => item.kind === 'turn' && item.id === turnId,
    )[0];
    const segment = owner?.segments.filter(seg => seg.id === segmentId && seg.kind === 'confirm')[0];
    if (!segment || segment.kind !== 'confirm') return;

    setConfirmState(turnId, segmentId, 'busy');
    if (optsRef.current.echoConfirm) {
      const verb = recommendation === 'confirm' ? 'Confirmed' : 'Canceled';
      update(cur => [
        ...cur,
        {
          kind: 'user',
          id: nextId(),
          time: 'just now',
          body: `${verb}: ${segment.summary}`,
          queued: false,
        },
      ]);
    }

    const replyTurnId = nextId();
    setStreamingBoth(true);
    update(cur => [...cur, emptyTurn(replyTurnId, optsRef.current.agentName)]);
    const handle = streamApi(confirmChatRoute, {
      body: { pending_call_id: segment.pending_call_id, recommendation },
      gracefulFallback: () => {
        setConfirmState(turnId, segmentId, 'error');
        failTurn(replyTurnId);
      },
    });
    await streamIntoTurn(replyTurnId, handle);
    setConfirmState(turnId, segmentId, 'done');
  }

  function appendNote(note: { agent: string; body: ReactNode; tag?: string }): void {
    update(cur => [
      ...cur,
      { kind: 'note', id: nextId(), agent: note.agent, body: note.body, tag: note.tag ?? null },
    ]);
  }

  function clearNotes(tag: string): void {
    update(cur => cur.filter(item => item.kind !== 'note' || item.tag !== tag));
  }

  return {
    items: itemsRef.current,
    streaming,
    send,
    stop,
    reset,
    confirm,
    appendNote,
    clearNotes,
  };
}
